'use client';

import * as React from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer/drawer';
import { useTenantSlug } from '@/hooks/use-tenant-slug';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxGroup {
  label: string;
  options: ComboboxOption[];
}

interface ComboboxProps {
  options?: ComboboxOption[];
  groups?: ComboboxGroup[];
  value?: string;
  onValueChange: (value: string) => void;
  onSearchChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Class name for the popover/drawer content (useful for dark themes) */
  popoverClassName?: string;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
  renderValue?: (option: ComboboxOption) => React.ReactNode;
  /** Custom content to render when search returns no results. Replaces emptyMessage. Receives a select handler to set value and close. */
  renderEmpty?: (select: (value: string) => void) => React.ReactNode;
  /**
   * Called with the trimmed search text when the combobox is dismissed without an
   * explicit selection (drawer swiped away, popover clicked away). Lets free-text
   * comboboxes commit what the user typed instead of silently discarding it.
   * Escape still cancels without committing.
   */
  onSearchCommit?: (search: string) => void;
  /**
   * Whether to show the search input integrated into the trigger when open.
   * Only applies on desktop — mobile always uses the drawer search bar.
   */
  searchInTrigger?: boolean;
  enterKeyHint?: React.HTMLAttributes<HTMLInputElement>['enterKeyHint'];
}

/**
 * Searchable single-select combobox component.
 *
 * Supports flat options via `options` or grouped options via `groups`.
 * Auto-adapts for mobile: opens as a Vaul drawer with searchable scrollable list.
 * On desktop: popover dropdown with search-in-trigger.
 */
export function Combobox({
  options,
  groups,
  value,
  onValueChange,
  onSearchChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  disabled = false,
  className,
  popoverClassName,
  renderOption,
  renderValue,
  renderEmpty,
  onSearchCommit,
  searchInTrigger = true,
  enterKeyHint,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hiddenInputRef = React.useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  // Light-theme tenants (DGD) get the data-theme-light attribute so the
  // portalled popover content picks up our CSS overrides instead of inheriting
  // the default dark popover styling.
  const tenantSlug = useTenantSlug();
  const themeLightAttr = tenantSlug === 'dgd' ? 'true' : undefined;

  // Flatten all options for lookup
  const allOptions = React.useMemo(() => {
    if (groups) {
      return groups.flatMap((g) => g.options);
    }
    return options ?? [];
  }, [options, groups]);

  const selectedOption = React.useMemo(
    () => allOptions.find((opt) => opt.value === value),
    [allOptions, value],
  );

  const updateSearchValue = (nextValue: string) => {
    setSearchValue(nextValue);
    onSearchChange?.(nextValue);
  };

  // Explicit selection / Escape must win over the dismiss-commit below, even when
  // Radix/Vaul fire their own onOpenChange(false) in the same tick (ref = sync).
  const suppressCommitRef = React.useRef(false);

  const handleSelect = (selectedValue: string) => {
    suppressCommitRef.current = true;
    onValueChange(selectedValue);
    updateSearchValue('');
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      suppressCommitRef.current = false;
    } else if (!suppressCommitRef.current && onSearchCommit) {
      const typed = searchValue.trim();
      if (typed) onSearchCommit(typed);
    }
    setOpen(nextOpen);
  };

  // Focus visible input when popover opens (desktop only)
  React.useEffect(() => {
    if (open && searchInTrigger && !isMobile && inputRef.current) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open, searchInTrigger, isMobile]);

  // Clear search when closed
  React.useEffect(() => {
    if (!open) {
      setSearchValue('');
      onSearchChange?.('');
    }
  }, [onSearchChange, open]);

  // Forward keyboard events from visible input to hidden CommandInput (desktop)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      suppressCommitRef.current = true;
      setOpen(false);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      if (hiddenInputRef.current) {
        const syntheticEvent = new KeyboardEvent('keydown', {
          key: e.key,
          code: e.code,
          bubbles: true,
        });
        hiddenInputRef.current.dispatchEvent(syntheticEvent);
      }
    }
  };

  const getDisplayContent = () => {
    if (!selectedOption) {
      return <span className="text-muted-foreground">{placeholder}</span>;
    }
    if (renderValue) {
      return renderValue(selectedOption);
    }
    return selectedOption.label;
  };

  const renderItems = (itemOptions: ComboboxOption[], mobileStyle = false) =>
    itemOptions.map((option) => {
      const isSelected = value === option.value;
      return (
        <CommandItem
          key={option.value}
          value={option.label}
          onSelect={() => handleSelect(option.value)}
          className={mobileStyle ? 'py-3 text-base min-h-[48px] rounded-none' : undefined}
        >
          {!mobileStyle && (
            <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
          )}
          {renderOption ? renderOption(option) : option.label}
          {mobileStyle && isSelected && <Check className="ml-auto h-4 w-4 opacity-70" />}
        </CommandItem>
      );
    });

  const renderGroupedContent = (mobileStyle = false) => {
    if (groups) {
      return groups.map((group, i) => (
        <React.Fragment key={group.label}>
          {i > 0 && <CommandSeparator />}
          <CommandGroup
            heading={group.label}
            className={cn(mobileStyle ? 'px-0' : undefined, 'text-inherit')}
          >
            {renderItems(group.options, mobileStyle)}
          </CommandGroup>
        </React.Fragment>
      ));
    }
    return (
      <CommandGroup className={cn(mobileStyle ? 'px-0' : undefined, 'text-inherit')}>
        {renderItems(allOptions, mobileStyle)}
      </CommandGroup>
    );
  };

  // Shared trigger button — same for mobile and desktop
  const triggerButton = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      className={cn(
        // text-base on mobile (16px) prevents iOS Safari auto-zoom; the visible search
        // input inherits this size (text-inherit), so it must be 16px on mobile too. text-sm on md+
        'flex w-full items-center justify-between rounded-md border border-input bg-transparent text-base md:text-sm ring-offset-background transition-colors',
        'data-[placeholder]:text-muted-foreground',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        'hover:bg-accent/50 hover:text-current',
        'disabled:cursor-not-allowed disabled:opacity-50',
        '[&>span]:line-clamp-1',
        'h-9 px-3 py-2 font-normal',
        className,
      )}
    >
      {open && searchInTrigger && !isMobile ? (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={searchValue}
            onChange={(e) => updateSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            enterKeyHint={enterKeyHint}
            className="flex-1 bg-transparent outline-none text-inherit placeholder:text-muted-foreground min-w-0"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : (
        <span className="flex-1 text-left truncate">{getDisplayContent()}</span>
      )}
      <ChevronDown
        className={cn(
          'ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform',
          open && 'rotate-180',
        )}
      />
    </Button>
  );

  // ── Mobile: Vaul drawer ──────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div onClick={() => !disabled && handleOpenChange(true)}>{triggerButton}</div>
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerContent
            data-theme-light={themeLightAttr}
            className={cn('max-h-[70dvh] flex flex-col overflow-hidden', popoverClassName)}
            onEscapeKeyDown={() => {
              suppressCommitRef.current = true;
            }}
          >
            <DrawerTitle className="sr-only">{placeholder}</DrawerTitle>
            <Command
              className={cn('flex flex-col flex-1 min-h-0 bg-transparent', popoverClassName)}
              shouldFilter={!onSearchChange}
            >
              <CommandInput
                value={searchValue}
                onValueChange={updateSearchValue}
                placeholder={searchPlaceholder}
                enterKeyHint={enterKeyHint}
                className="h-11 text-base"
              />
              <CommandList className="flex-1 overflow-y-auto max-h-none overscroll-contain">
                <CommandEmpty className="py-12 text-center text-sm opacity-50">
                  {renderEmpty ? renderEmpty(handleSelect) : emptyMessage}
                </CommandEmpty>
                {renderGroupedContent(true)}
              </CommandList>
            </Command>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // ── Desktop: Popover ─────────────────────────────────────────────────
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        data-theme-light={themeLightAttr}
        className={cn('p-0', popoverClassName)}
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        onWheel={(e) => e.stopPropagation()}
        onEscapeKeyDown={() => {
          suppressCommitRef.current = true;
        }}
        onOpenAutoFocus={(e) => {
          if (searchInTrigger) e.preventDefault();
        }}
      >
        <Command
          className={cn(popoverClassName, searchInTrigger && '[&_[cmdk-input-wrapper]]:hidden')}
          shouldFilter={!onSearchChange}
        >
          <CommandInput
            ref={hiddenInputRef}
            value={searchValue}
            onValueChange={updateSearchValue}
            tabIndex={-1}
          />
          <CommandList className="max-h-[260px]" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>{renderEmpty ? renderEmpty(handleSelect) : emptyMessage}</CommandEmpty>
            {renderGroupedContent(false)}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
